import { useEffect, useState } from "react";
import Shell from "../components/Shell";
import { api, API_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { statusClass, toDDMMYYYY } from "../lib/helpers";
import { Download, Car, Calendar, Settings as SettingsIcon, X, Loader2 } from "lucide-react";

function Bars({ data, color = "#0284C7" }) {
  const entries = Object.entries(data || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="space-y-2.5">
      {entries.length === 0 ? <div className="text-sm text-slate-400">No data.</div> :
        entries.sort((a, b) => b[1] - a[1]).map(([k, v]) => (
          <div key={k}>
            <div className="flex justify-between text-xs font-medium text-slate-700 mb-1">
              <span>{k.replace("_", " ")}</span>
              <span className="text-slate-500">{v}</span>
            </div>
            <div className="h-2 bg-slate-100 rounded">
              <div className="h-2 rounded" style={{ background: color, width: `${(v / max) * 100}%` }} />
            </div>
          </div>
        ))
      }
    </div>
  );
}

function defaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

function TravelPanel() {
  const { user } = useAuth();
  const [range, setRange] = useState(defaultDateRange());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTick, setSettingsTick] = useState(0);  // bump after a settings save → refresh data

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api.get(`/travel/summary?from=${range.from}&to=${range.to}`);
      if (cancelled) return;
      setData(r.data);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to, settingsTick]);

  const onExport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("sohom_token");
      const res = await fetch(`${API_URL}/travel/export.xlsx?from=${range.from}&to=${range.to}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `travel_${range.from}_to_${range.to}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  // Group rows by date for compact day-wise display
  const rowsByDate = {};
  (data?.rows || []).forEach((r) => {
    (rowsByDate[r.date] ||= []).push(r);
  });
  const dates = Object.keys(rowsByDate).sort().reverse();

  return (
    <div className="space-y-5">
      <div className="sohom-card p-5">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">From</label>
              <input type="date" className="sohom-input mt-1" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="travel-from" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">To</label>
              <input type="date" className="sohom-input mt-1" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="travel-to" />
            </div>
          </div>
          <button onClick={onExport} disabled={downloading || !data?.rows?.length} className="sohom-btn-secondary" data-testid="travel-export-btn">
            <Download className="w-4 h-4" /> {downloading ? "Exporting…" : "Export Excel"}
          </button>
          {user?.role === "admin" && (
            <button onClick={() => setShowSettings(true)} className="sohom-btn-secondary" data-testid="travel-settings-btn">
              <SettingsIcon className="w-4 h-4" /> Settings
            </button>
          )}
        </div>
        {data && (
          <div className="text-[11px] text-slate-500 mt-3">
            Distance counts only <strong>new leads created with &ldquo;Use my location&rdquo;</strong> and <strong>&ldquo;I&apos;m here&rdquo; check-ins</strong> — call / admin follow-ups are not counted. Office round-trip is added on any day with at least one site pin.
            Multiplier: <strong>{data.multiplier}</strong>{data.effective_from ? <> · Effective from: <strong>{toDDMMYYYY(data.effective_from)}</strong></> : null}. Days are bucketed in IST.
          </div>
        )}
      </div>

      {showSettings && (
        <TravelSettingsDialog onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); setSettingsTick((t) => t + 1); }} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sohom-card p-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Total KM</div>
          <div className="text-3xl font-bold mt-2 text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}} data-testid="travel-total-km">
            {data?.total_km ?? "…"} <span className="text-base font-normal text-slate-500">km</span>
          </div>
        </div>
        <div className="sohom-card p-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Active Days</div>
          <div className="text-3xl font-bold mt-2 text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>{data?.total_days ?? "…"}</div>
        </div>
        <div className="sohom-card p-5">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{user?.role === "field_executive" ? "You" : "Field Executives"}</div>
          <div className="text-3xl font-bold mt-2 text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>{data?.user_count ?? "…"}</div>
        </div>
      </div>

      <div className="sohom-card overflow-hidden" data-testid="travel-table">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Day-wise Travel</h3>
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-slate-400">Loading…</div>
        ) : dates.length === 0 ? (
          <div className="px-5 py-10 text-center text-slate-400">
            No GPS-tagged lead activity in this range. Distances are derived from
            field execs dropping &ldquo;Use my location&rdquo; when creating leads.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="sohom-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Field Executive</th>
                  <th>Visits</th>
                  <th>Office round-trip</th>
                  <th>Total distance</th>
                  <th>Lead Numbers</th>
                </tr>
              </thead>
              <tbody>
                {dates.map((date) => rowsByDate[date].map((r) => (
                  <tr key={`${date}-${r.user_id}`} data-testid={`travel-row-${date}-${r.user_id}`}>
                    <td className="font-mono text-slate-700 whitespace-nowrap">{toDDMMYYYY(date)}</td>
                    <td className="font-medium text-slate-900">{r.user_name}</td>
                    <td>{r.visit_count}</td>
                    <td className="text-slate-600">{r.office_legs_km ? `${r.office_legs_km} km` : <span className="text-slate-300 italic text-xs">not set</span>}</td>
                    <td className="font-semibold text-sky-700">{r.distance_km} km</td>
                    <td className="text-xs text-slate-500">{r.visits.join(", ")}</td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPanel({ data }) {
  if (!data) return <div className="text-slate-400">Loading…</div>;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="sohom-card p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">By Status</h3>
          <Bars data={data.by_status} color="#0284C7" />
        </div>
        <div className="sohom-card p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">By Area</h3>
          <Bars data={data.by_area} color="#00AEEF" />
        </div>
        <div className="sohom-card p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-4">By User</h3>
          <Bars data={data.by_user} color="#10B981" />
        </div>
      </div>
      <div className="mt-6 sohom-card p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Grand Total</div>
        <div className="text-4xl font-bold text-slate-900 mt-1" style={{fontFamily:"'Outfit', sans-serif"}}>{data.total}</div>
      </div>
    </>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("status");
  const [status, setStatus] = useState(null);

  useEffect(() => {
    api.get("/reports/summary").then((r) => setStatus(r.data));
  }, []);

  return (
    <Shell>
      <div className="px-4 sm:px-8 py-5 sm:py-7 max-w-6xl">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-1" style={{fontFamily:"'Outfit', sans-serif"}}>Reports</h1>
        <p className="text-sm text-slate-500 mb-5">Aggregated counts across your accessible leads, plus field-executive travel for fuel reimbursement.</p>

        <div className="flex gap-1 mb-5 border-b border-slate-200" data-testid="reports-tabs">
          <button
            onClick={() => setTab("status")}
            data-testid="tab-status"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              tab === "status" ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Calendar className="w-4 h-4" /> Pipeline
          </button>
          <button
            onClick={() => setTab("travel")}
            data-testid="tab-travel"
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              tab === "travel" ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            <Car className="w-4 h-4" /> Travel
          </button>
        </div>

        {tab === "status" ? <StatusPanel data={status} /> : <TravelPanel />}
      </div>
    </Shell>
  );
}

function TravelSettingsDialog({ onClose, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [multiplier, setMultiplier] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [geofenceM, setGeofenceM] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await api.get("/travel/settings");
      if (cancelled) return;
      setSettings(r.data);
      setMultiplier(String(r.data.multiplier));
      setEffectiveFrom(r.data.effective_from);
      setGeofenceM(String(r.data.geofence_m ?? r.data.default_geofence_m ?? 150));
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const m = parseFloat(multiplier);
      if (!Number.isFinite(m) || m <= 0 || m > 5) { setErr("Multiplier must be between 0 and 5."); setSaving(false); return; }
      const gf = parseFloat(geofenceM);
      if (!Number.isFinite(gf) || gf < 10 || gf > 5000) { setErr("Geofence radius must be between 10 and 5000 metres."); setSaving(false); return; }
      await api.patch("/travel/settings", {
        multiplier: m,
        effective_from: effectiveFrom,
        geofence_m: gf,
      });
      onSaved();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (!settings) return;
    setMultiplier(String(settings.default_multiplier));
    setEffectiveFrom(settings.default_effective_from);
    setGeofenceM(String(settings.default_geofence_m ?? 150));
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="travel-settings-dialog">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900" style={{fontFamily:"'Outfit', sans-serif"}}>Travel Settings</h2>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {!settings ? (
            <div className="text-sm text-slate-400 italic">Loading…</div>
          ) : (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Road-distance multiplier</label>
                <input
                  type="number" step="0.01" min="0.1" max="5"
                  className="sohom-input mt-1"
                  value={multiplier}
                  onChange={(e) => setMultiplier(e.target.value)}
                  data-testid="settings-multiplier"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Applied to every straight-line GPS distance to approximate real road distance. Calibrate using a few known routes (Google Maps road distance ÷ straight-line haversine).
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Effective from</label>
                <input
                  type="date"
                  className="sohom-input mt-1"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                  data-testid="settings-effective-from"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Pins recorded before this date are excluded from all travel reports. Set to the date the GPS-tracking workflow was correctly established.
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Check-in geofence radius (metres)</label>
                <input
                  type="number" step="10" min="10" max="5000"
                  className="sohom-input mt-1"
                  value={geofenceM}
                  onChange={(e) => setGeofenceM(e.target.value)}
                  data-testid="settings-geofence"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  How close an FE must be to a lead's site (for "I'm here") or to their saved office (for "At office") to check in. Lower is stricter; too low may reject valid check-ins due to normal GPS drift (typical phone accuracy is 10–30 m).
                </div>
              </div>
              {settings.updated_by_name && (
                <div className="text-[11px] text-slate-500 border-t pt-3">
                  Last updated by <strong>{settings.updated_by_name}</strong> at {new Date(settings.updated_at).toLocaleString()}
                </div>
              )}
              {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{err}</div>}
            </>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-between gap-2">
          <button onClick={resetToDefaults} className="sohom-btn-secondary text-xs" disabled={!settings} data-testid="settings-reset-defaults">
            Reset to defaults
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="sohom-btn-secondary">Cancel</button>
            <button onClick={save} disabled={saving || !settings} className="sohom-btn-primary" data-testid="settings-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// statusClass re-exported for tree-shake awareness (used inside StatusPanel via JSX className calls in future tweaks)
export { statusClass };
